//@ts-check
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
//import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import { Rhino3dmLoader } from "./3DMLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import viewerMgrInstance from "./ViewerMgr.js";


/**
 * Initiates a new File Loader
 * @class
 */
class FileLoader {
  /**
   * 
   * @constructs FileLoader
   * @param {*}  viewerInstance - Viewer Instance 
   */
  constructor(viewerInstance) {
    this.registered = {};
    this.viewer = viewerInstance;
    this.file = null;
    this.fileExtension = null;
    this.extension = null;
    this.mtlFile = null;
    this.objFile = null;
    this.fileName = null;
    this.fileType = null;
    this.arrayOfModel = [];
    this.group = new THREE.Group();
    this.loadProgress = 0;
  }
  /**
   * Clears the Models
   */
  clearModels() {
    const scope = this;
    scope.arrayOfModel = [];
    scope.group = new THREE.Group();
  }
  /**
   * Adds Event Listener
   * @param {string} name - Name of the event
   * @param {Object} callback - Callback function
   */
  addEventListener(name, callback) {
    if (!this.registered[name]) this.registered[name] = [];
    this.registered[name].push(callback);
  }
  /**
   * Triggers Event
   * @param {string} name 
   * @param {Object} [args = []] 
   */
  triggerEvent(name, args = []) {
    //console.log(`Triggering event: ${name}`, args);
    
    if (!this.registered[name]) {
      console.warn(`No handlers registered for event: ${name}`);
      this.registered[name] = [];
      return;
    }
    
    if (name === "load-progress" && args.length > 0) {
      // For progress events, pass all parameters directly to the handler
      this.registered[name].forEach((fnc) => {
        try {
          // Pass both percentage and message if they exist
          if (args.length >= 2) {
            fnc(args[0], args[1]);
          } else {
            fnc(args[0]);
          }
        } catch (err) {
          console.error(`Error in load-progress handler:`, err);
        }
      });
    } else {
      // For other events, use apply
      this.registered[name].forEach((fnc) => {
        try {
          fnc.apply(this, args);
        } catch (err) {
          console.error(`Error in ${name} handler:`, err);
        }
      });
    }
  }


  /**
   * Sets Objects - Mutiple Model loading
   * @param {Array<THREE.Group>} arrayOfModel 
   */
  setObjects(arrayOfModel) {
    const scope = this;
    arrayOfModel.forEach((object) => {
      scope.group.add(object);
    });
    scope.setObject(scope.group);
    scope.viewer.setBounds();
    scope.viewer.addGround();
    scope.viewer.setSkyPosition();
    scope.viewer.recalculateShadows();
    scope.viewer.updateEdges(false);
    scope.viewer.arrayOfModel = arrayOfModel;
    viewerMgrInstance.render();
  }


  /**
   * Sets Objects
   * @param {THREE.Group} object 
   */
  setObject(object) {
    const scope = this;
    scope.addModelInViewer(object);
    scope.calculateBoundingBoxOfModel();
    viewerMgrInstance.render();
  }

  /**
   * Computes Bounding Box Efficiently
   * @returns {THREE.Box3} - Bounding Box
   */
  computeBoundingBoxEfficiently() {
    const scope = this;
    const boundingBox = new THREE.Box3();

    scope.viewer.elementsMap.forEach((child, name) => {
      if (child.isMesh) {
        if (child.isMesh && !child.geometry.boundingBox) {
          child.geometry.computeBoundingBox();  // Compute only once
        }
        boundingBox.union(child.geometry.boundingBox);
      }
    })
    return boundingBox;
  }


  /**
   * Calculates Bounding box of the loaded model
   * @param {Number} [percentage = 1]  - Percentage
   */
  calculateBoundingBoxOfModel( percentage = 1) {
    const scope = this;
    var box = scope.computeBoundingBoxEfficiently();
    var sphere = box.getBoundingSphere(new THREE.Sphere());

    var r = sphere.radius * percentage,
      offset = r / Math.tan(Math.PI / 180.0 * scope.viewer.orbitControls.object.fov * 0.5),
      vector = new THREE.Vector3(-1, -1, 1),
      dir = vector,
      newPos = new THREE.Vector3();
    dir.multiplyScalar(offset * 0.75);
    newPos.addVectors(sphere.center, dir);
    var target = new THREE.Vector3(sphere.center.x, sphere.center.y, sphere.center.z);

    scope.viewer.camera.position.set(newPos.x, newPos.y, newPos.z);
    scope.viewer.camera.lookAt(target);
    scope.viewer.camera.updateProjectionMatrix();

    scope.viewer.orbitControls.object.position.set(newPos.x, newPos.y, newPos.z);
    scope.viewer.orbitControls.target = target;
    scope.viewer.orbitControls.update();
  }

  /**
   * Add Model in Viewer : Recursive custom traverse
   * @param {THREE.Group} object 
   */
  addModelInViewer(object) {
    const scope = this;
    scope.viewer.modelScene.add(object);


    /**
     * the custom recursive traversal function
     * @param {THREE.Object3D} object 
     * @param {*} callback 
     */
    function customTraverse(object, callback) {
      callback(object);  // Process the current object
      object.children.forEach(child => {
        customTraverse(child, callback);  // Recursively call for each child
      });
    }

    // Traverse the model scene and apply logic to each child object
    customTraverse(scope.viewer.modelScene, function (child) {
      if (child instanceof THREE.Mesh) {
        child.layers.enable(1);
        child.castShadow = true;
        child.receiveShadow = true;
        scope.viewer.elementsMap.set(child.name, child);
        child.geometry.computeBoundingBox();
        const edges = new THREE.EdgesGeometry(child.geometry, 15);
        const line = new THREE.LineSegments(edges, scope.viewer.edgeMaterial);
        scope.viewer.edgesMap.set(child.name, line);
        scope.viewer.edgesGroup.add(line);
      }

      if (child.material) {
        scope.viewer.model.add(child.clone());
        scope.viewer.modelMap.set(child.name, child.clone());
      }
    });
    viewerMgrInstance.render();
  }


  /**
   * Zoom Extent 
   * @returns {Object} Return success or error messages 
   */
  zoomAll() {
    const scope = this;
    try {
      if (scope.viewer.modelScene.children[0]) {
        scope.calculateBoundingBoxOfModel(scope.viewer.modelScene.children[0]);
        viewerMgrInstance.render();

       scope.viewer.setBounds();
        var returnValue = {
          "status": "success",
          "message": "Model has been focused."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load model in viewer."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error " + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }


  /**
   * Add Model from User's files
   * @param {*} files 
   * @returns {Object} Return success or error messages 
   */
  addModel(files) {
    const scope = this;
    try {
      if (files != undefined) {
        for (const element of files) {
          this.fileName = element.name;
          this.fileType = this.fileName
            .substring(this.fileName.lastIndexOf(".") + 1)
            .toLowerCase();
          if (this.fileType == "gltf" || this.fileType == "glb") {
            this.gltfFileLoad(element);
          } else if (this.fileType == "3dm") {
            this.rhino3dmFileLoad(element);
          } else if (this.fileType == "obj") {
            this.objFileLoad(element);
          }
        }
        
        var returnValue = {
          "status": "success",
          "message": "Model loaded."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Add file to load in viewer."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": " System Error: " + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }


  /**
   * GLTF File Loader
   * @param {*} file 
   * @returns {Object} Return success or error messages 
   */
  gltfFileLoad(file) {
    console.time('gltfFileLoad - onload');
    const scope = this;
    try {
      let fileName = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
      if (fileName == "gltf" || fileName == "glb") {
        var gltfLoader = new GLTFLoader();
        this.fileURL = URL.createObjectURL(file);
        if (this.fileURL) {
          gltfLoader.load(this.fileURL, (gltf) => {
            console.timeEnd('gltfFileLoad - onload');
            if (scope.viewer.isModelDisposed) scope.clearModels();
            scope.arrayOfModel.push(gltf.scene);
            scope.setObjects(scope.arrayOfModel);
            scope.triggerEvent("load-finished");
          });
        }
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "GLTF or GLB Model loaded."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load GLTF or GLB model."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error: " + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }

  /**
   * GLTF Loader from Url
   * @param {string} url 
   * @returns {Object} Return success or error messages 
   */
  gltfFileLoadfromURL(url) {
    const scope = this;
    try {
      let fileName = url.substring(url.lastIndexOf(".") + 1).toLowerCase();
      if (fileName == "gltf" || fileName == "glb") {
        var gltfLoader = new GLTFLoader();
        this.fileURL = url;
        if (this.fileURL) {
          gltfLoader.load(this.fileURL, (gltf) => {
            if (scope.viewer.isModelDisposed) scope.clearModels();
            scope.arrayOfModel.push(gltf.scene);
            scope.setObjects(scope.arrayOfModel);
            this.triggerEvent("load-finished");
          });
        }
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "GLTF or GLB Model loaded from URL."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load GLTF or GLB model from URL."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error :" + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }

  //* https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader

  /**
   * Rhino3dm File Loader
   * https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader
   * @param {*} file 
   * @returns {Object} Return success or error messages 
   */
  rhino3dmFileLoad(file) {
    console.time('rhino3dmFileLoad - onload');
    const scope = this;
    try {
      let fileName = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
      if (fileName == "3dm") {
        var rhino3dmLoader = new Rhino3dmLoader();
        rhino3dmLoader.setLibraryPath('https://cdn.jsdelivr.net/npm/rhino3dm@8.17.0/');
        //rhino3dmLoader.setWorkerLimit(12);
        if (file) {
          this.fileURL = URL.createObjectURL(file);
          if (this.fileURL) {
            rhino3dmLoader.load(this.fileURL, (object) => {
              console.timeEnd('rhino3dmFileLoad - onload');
              object.name = file.name;
              if (scope.viewer.isModelDisposed) scope.clearModels();
              scope.arrayOfModel.push(object);
              scope.setObjects(scope.arrayOfModel);
              this.triggerEvent("load-finished");
            },
              function (xhr) {
                //console.log((xhr.loaded / xhr.total) * 100 + "% loaded");
              },
              // called when loading has errors
              function (error) {
                var returnValue = {
                  "status": "failed",
                  "message": "Error during Rhino3dmLoader:" + error
                }
                return returnValue;
              });
          }
        }
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "3DM Model loaded."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load 3DM model."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error :" + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }

  //* https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader
  /**
   * Rhino3dm loader from Url
   * https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader
   * @param {string} url - Url 
   * @param {string} name - Name of the model
   * @returns {Object} Return success or error messages 
   */
  rhino3dmFileLoadfromUrl(url, name) {
    console.time('onload');
    const scope = this;
    try {
      //let fileName = url.substring(url.lastIndexOf(".") + 1).toLowerCase();
      let fileName = url.substring((url.lastIndexOf(".") + 1), (url.lastIndexOf(".") + 4)).toLowerCase();
      if (fileName == "3dm") {
        this.fileURL = url;
        var rhino3dmLoader = new Rhino3dmLoader();
        
        // Set library path 
        rhino3dmLoader.setLibraryPath('https://cdn.jsdelivr.net/npm/rhino3dm@8.17.0/');
        
        
        // Set worker limit
        rhino3dmLoader.setWorkerLimit(4);

        // Initial progress update
        scope.loadProgress = 5;
        this.triggerEvent("load-progress", [scope.loadProgress, "Starting download..."]);

        // Track processing stage and progress separately from download
        scope.downloadComplete = false;
        scope.processingStarted = false;
        scope.processingProgress = 0;

        // Add detailed progress tracking
        rhino3dmLoader.load(
          this.fileURL,
          // onLoad callback
          (object) => {
            console.timeEnd('onload');
            //console.log("Model data received, starting processing:", name);
            
            scope.downloadComplete = true;
            
            // Update progress to show we're entering the post-processing phase
            scope.loadProgress = 60; // Model data is loaded but still needs processing
            
            this.triggerEvent("load-progress", [scope.loadProgress, "Processing model data..."]);
            
            //console.log(object, name);
            // Model data is loaded but we need to process it
            object.name = name;
            
            // Count meshes for progress tracking
            let totalMeshes = object.children.length || 0;
            //console.log(`Total meshes to process: ${totalMeshes}`);
            console.time('processModel')
            // Now process the model with progress updates
            this.processModel(object, totalMeshes);
            console.timeEnd('processModel')
          },
          // onProgress callback
          (xhr) => {
            if (xhr.lengthComputable && !scope.downloadComplete) {
              // Download progress is tracked from 5% to 60% 
              const downloadPercent = Math.round((xhr.loaded / xhr.total) * 100);
              
              // Prevent the progress bar from jumping backwards
              const progressValue = Math.max(scope.loadProgress, 5 + Math.round(downloadPercent * 0.55));
              
              scope.loadProgress = progressValue;
              const bytesLoaded = this.formatBytes(xhr.loaded);
              const bytesTotal = this.formatBytes(xhr.total);
              
              this.triggerEvent("load-progress", [
                progressValue, 
                `Downloading model data: ${downloadPercent}% (${bytesLoaded}/${bytesTotal})`
              ]);
              
              // If we're close to completion, prepare for the processing phase
              if (downloadPercent >= 98) {
                this.triggerEvent("load-progress", [60, "Download complete, converting to web..."]);
              }
            }
          },
          (error) => {
            console.error("Error loading 3DM model:", error);
                scope.loadProgress = 0;
    
            // Log more diagnostic information
            if (error && error.message) {
                console.error("Error details:", error.message);
                
                // Try to specifically handle instanceDefinitions error
                if (error.message.includes("instanceDefinitions") && error.message.includes("count")) {
                    console.warn("Detected instanceDefinitions.count issue - likely version mismatch");
                    error = {
                        originalError: error,
                        message: "This model contains instance definitions that are incompatible with the current 3D viewer. Try exporting with a different version of Rhino or converting to a different format."
                    };
                }
            }
            
            this.triggerEvent("load-error", [error]);
          }
        );

        rhino3dmLoader.dispose();
        
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "3DM Model load started from URL."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load 3DM model from URL."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error :" + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }
  
  /**
   * Process the model with progress updates
   * @param {THREE.Object3D} object - The model object
   * @param {Number} totalMeshes - Total number of meshes in the model
   */
  processModel(object, totalMeshes) {
    const scope = this;
    const startTime = performance.now();
    
    // First, collect all children to track overall progress
    const allObjects = [];
    object.traverse((child) => {
      allObjects.push(child);
    });
    
    const totalChildren = allObjects.length;
    //console.log(`Total children in scene: ${totalChildren}`);
    
    // Start the processing phase
    scope.triggerEvent("load-progress", [65, `Processing scene graph (0/${totalChildren})...`]);
    
    // Add to scene with progress tracking
    if (scope.viewer.isModelDisposed) scope.clearModels();
    scope.arrayOfModel.push(object);
    
    // Process in smaller batches to avoid blocking the UI
    const BATCH_SIZE = 100;
    let processedCount = 0;
    let processedMeshes = 0;
    
    // Process a batch of objects and allow UI to update between batches
    const processBatch = () => {
      //console.log(`Processing batch starting at ${processedCount}`);
      
      // Process a limited number of objects in this batch
      const endIndex = Math.min(processedCount + BATCH_SIZE, totalChildren);
      
      // Process this batch
      for (let i = processedCount; i < endIndex; i++) {
        const child = allObjects[i];
        
        // Also track meshes separately
        if (child instanceof THREE.Mesh) {
          processedMeshes++;
        }
      }
      
      // Update processed count
      processedCount = endIndex;
      
      // Update progress display
      const elapsedTime = ((performance.now() - startTime) / 1000).toFixed(1);
      const processingPercent = Math.round((processedCount / totalChildren) * 100);
      const progressValue = 65 + Math.round((processingPercent * 30) / 100);
      
      //console.log(`Progress update: ${progressValue}%, Processed ${processedCount}/${totalChildren}, elapsed ${elapsedTime}s`);
      
      scope.loadProgress = progressValue;
      scope.triggerEvent("load-progress", [
        progressValue,
        `Processing scene graph: ${processingPercent}% (${processedCount}/${totalChildren}) - ${elapsedTime}s`
      ]);
      
      // Continue if there are more objects to process
      if (processedCount < totalChildren) {
        // Use setTimeout to yield to the UI thread
        setTimeout(processBatch, 0);
      } else {
        // All objects processed, now set up the scene
        //console.log("All objects processed, setting up scene");
        
        // Actually set up the scene
        scope.triggerEvent("load-progress", [85, "Building scene geometry..."]);
        
        // Use setTimeout to allow the UI to update before the heavy scene setup operation
        setTimeout(() => {
          //console.time('setObjects');
          scope.setObjects(scope.arrayOfModel);
          //console.timeEnd('setObjects');
          
          // Final stages
          scope.triggerEvent("load-progress", [95, "Finalizing scene..."]);
          
          // Small delay to show completion
          setTimeout(() => {
            scope.loadProgress = 100;
            scope.triggerEvent("load-progress", [100, "Model loaded successfully!"]);
            scope.triggerEvent("load-finished");
          }, 100);
        }, 50);
      }
    };
    
    // Start the first batch after a small delay
    setTimeout(processBatch, 50);
  }

  /**
   * Format bytes to human-readable format
   * @param {Number} bytes - Number of bytes
   * @returns {String} - Formatted string
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  //* https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader 
  /**
   * Rhino3dm loader from buffer
   * https://threejs.org/docs/index.html?q=3dm#examples/en/loaders/3DMLoader
   * @param {Object} buffer - buffer  
   * @returns {Object} Return success or error messages 
   */
  rh3dmFileLoadByteArray(buffer) {
    console.time('onload');
    const scope = this;
    //const buffer = byteArray.buffer;
    var rhino3dmLoader = new Rhino3dmLoader();
    rhino3dmLoader.setWorkerLimit(8);
    rhino3dmLoader.setLibraryPath(
      "https://cdn.jsdelivr.net/npm/rhino3dm@8.17.0/"
    );

    rhino3dmLoader.parse(
      buffer,
      (object) => {
        console.timeEnd('onload');
        if (scope.viewer.isModelDisposed) scope.clearModels();
        scope.arrayOfModel.push(object);
        scope.setObjects(scope.arrayOfModel);
        this.triggerEvent("load-finished");
      },
      (error) => {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load buffer Array: " + error
        }
        return returnValue;
      }
    )

    viewerMgrInstance.render();
    var returnValue = {
      "status": "success",
      "message": "3DM Model loaded from buffer array."
    }
    return returnValue;
  }

  /**
   * OBJ+MTL file loading
   * @param {*} mtlFile 
   * @param {*} objFile 
   * @returns {Object} Return success or error messages 
   */
  mtlFileLoad(mtlFile, objFile) {
    const scope = this;
    try {
      let mtlFileName = mtlFile.substring(mtlFile.lastIndexOf(".") + 1).toLowerCase();
      let objFileName = objFile.substring(objFile.lastIndexOf(".") + 1).toLowerCase();
      if (mtlFileName == "mtl" && objFileName == "obj") {
        var mtlLoader = new MTLLoader();
        if (mtlFile) {
          let mtlUrl = URL.createObjectURL(mtlFile);
          if (mtlUrl) {
            mtlLoader.load(mtlUrl, function (materials) {
              materials.preload();
              var objLoader = new OBJLoader();
              objLoader.setMaterials(materials);
              if (objFile) {
                let objUrl = URL.createObjectURL(objFile);
                if (objUrl) {
                  objLoader.load(objUrl, (object) => {
                    if (scope.viewer.isModelDisposed) scope.clearModels();
                    scope.arrayOfModel.push(object);
                    scope.setObjects(scope.arrayOfModel);
                    this.triggerEvent("load-finished");
                  });
                }
              }
            });
          }
        }
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "OBJ model with MTL file loaded."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load OBJ model with MTL file."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error:" + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }

  
  /**
   * Obj file loading
   * @param {*} file 
   * @returns {Object} Return success or error messages 
   */
  objFileLoad(file) {
    const scope = this;
    try {
      let fileName = file.substring(file.lastIndexOf(".") + 1).toLowerCase();
      if (fileName == "obj") {
        var objLoader = new OBJLoader();
        if (file) {
          this.fileURL = URL.createObjectURL(file);
          if (file) {
            objLoader.load(this.fileURL, (object) => {
              if (scope.viewer.isModelDisposed) scope.clearModels();
              scope.arrayOfModel.push(object);
              scope.setObjects(scope.arrayOfModel);
              scope.triggerEvent("load-finished");
            });
          }
        }
        viewerMgrInstance.render();
        var returnValue = {
          "status": "success",
          "message": "OBJ model loaded."
        }
        return returnValue;
      }
      else {
        var returnValue = {
          "status": "failed",
          "message": "ERROR : Please load OBJ model."
        }
        return returnValue;
      }
    }
    catch (err) {
      var returnValue = {
        "status": "failed",
        "message": "System Error: " + err.message
      }
      console.error(err.message);
      return returnValue;
    }
  }
}

export { FileLoader };